import { walutaDlaKraju } from '@insolitum/types';
import {
  CreditCard,
  Receipt,
  Download,
  AlertTriangle,
  CheckCircle,
  Clock,
  Loader2,
  RefreshCw,
  Building2,
  FileText,
  ExternalLink,
  Package,
  Truck,
  Zap,
  Crown,
} from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ZaplanowanaZmianaPlanu } from '../../components/admin/ZaplanowanaZmianaPlanu';
import { usePotwierdzenieZmianCennika } from '../../components/cennik/PotwierdzenieZmianyCennika';
import { ZmianyCennikaKlienta } from '../../components/cennik/ZmianyCennika';
import { useBilling } from '../../hooks/useBilling';
import type { BillingPlan } from '../../hooks/useBilling';
import { linijkaBrutto, sufiksNetto } from '../../lib/cenaOprogramowania';
import { supabase } from '../../lib/supabase';
import { dodajKwote, formatujKwotyWgWalut, KwotyWgWalut } from '../../lib/waluty';
import { okresAbonamentu, zapowiedzZmianyOkresu } from '../../lib/zaplanowanaZmiana';
import { getModuleDef } from '../../modules/registry';
import { useAuth } from '../../providers/AuthProvider';

// ─── Plan configuration ─────────────────────────────────────────────────────

const PLAN_ORDER: BillingPlan[] = ['free', 'starter', 'professional', 'enterprise'];

// Plan Universe to zasoby systemu, nie organy (797): organy kupuje się osobno.
const PLAN_URUCHAMIA_ORGANY =
  'Uruchamia organy i integracje od planu Start (każdy organ płatny osobno)';

// Default plan details (used as fallback when DB prices are not loaded)
const DEFAULT_PLAN_DETAILS: Record<
  BillingPlan,
  {
    name: string;
    monthlyPrice: number;
    yearlyPrice: number;
    features: string[];
    color: string;
    currency: string;
  }
> = {
  free: {
    name: 'Darmowy',
    monthlyPrice: 0,
    yearlyPrice: 0,
    features: ['Do 5 kont', '1 lokalizacja', '3 czujniki'],
    color: 'gray',
    currency: 'PLN',
  },
  starter: {
    name: 'Starter',
    monthlyPrice: 479,
    yearlyPrice: 4790,
    features: [
      'Lokalizacje bez limitu',
      '3 miejsca współbieżne (floating)',
      '1 urządzenie',
      '300 iTokenów/mc',
      PLAN_URUCHAMIA_ORGANY,
    ],
    color: 'blue',
    currency: 'PLN',
  },
  professional: {
    name: 'Professional',
    monthlyPrice: 1190,
    yearlyPrice: 11900,
    features: [
      'Lokalizacje bez limitu',
      '10 miejsc współbieżnych (floating)',
      '3 urządzenia + 1 TV',
      '2000 iTokenów/mc',
      PLAN_URUCHAMIA_ORGANY,
    ],
    color: 'purple',
    currency: 'PLN',
  },
  enterprise: {
    name: 'Enterprise',
    monthlyPrice: 2490,
    yearlyPrice: 24900,
    features: [
      'Lokalizacje bez limitu',
      'Miejsca współbieżne bez limitu',
      '10 urządzeń + 3 TV',
      '8000 iTokenów/mc',
      PLAN_URUCHAMIA_ORGANY,
    ],
    color: 'amber',
    currency: 'PLN',
  },
};

interface DbPlanPrice {
  plan: string;
  name_pl?: string;
  billing_interval: string;
  currency: string;
  amount: number;
  features_pl: string[];
}

const PLAN_COLORS: Record<string, { badge: string; icon: string; border: string }> = {
  free: {
    badge: 'bg-gray-500/20 text-gray-400',
    icon: 'text-gray-400',
    border: 'border-gray-600',
  },
  starter: {
    badge: 'bg-blue-500/20 text-blue-400',
    icon: 'text-blue-400',
    border: 'border-blue-500/50',
  },
  professional: {
    badge: 'bg-purple-500/20 text-purple-400',
    icon: 'text-purple-400',
    border: 'border-purple-500/50',
  },
  enterprise: {
    badge: 'bg-amber-500/20 text-amber-400',
    icon: 'text-amber-400',
    border: 'border-amber-500/50',
  },
};

// ─── Status mappings ─────────────────────────────────────────────────────────

const SUBSCRIPTION_STATUS: Record<string, { bg: string; label: string }> = {
  active: { bg: 'pill--active', label: 'Aktywna' },
  trialing: { bg: 'pill--trial', label: 'Okres próbny' },
  past_due: { bg: 'pill--error', label: 'Zaległa płatność' },
  cancelled: { bg: 'bg-aurora-bg-subtle text-aurora-muted', label: 'Anulowana' },
};

const INVOICE_STATUS: Record<string, { bg: string; icon: typeof CheckCircle; label: string }> = {
  paid: { bg: 'pill--active', icon: CheckCircle, label: 'Opłacona' },
  open: { bg: 'pill--trial', icon: Clock, label: 'Otwarta' },
  draft: { bg: 'bg-aurora-bg-subtle text-aurora-muted', icon: FileText, label: 'Szkic' },
  void: { bg: 'bg-aurora-bg-subtle text-aurora-muted', icon: AlertTriangle, label: 'Anulowana' },
  uncollectible: { bg: 'pill--error', icon: AlertTriangle, label: 'Nieściągalna' },
  payment_failed: {
    bg: 'pill--error',
    icon: AlertTriangle,
    label: 'Płatność nieudana',
  },
};

const ORDER_STATUS: Record<string, { bg: string; label: string }> = {
  pending: { bg: 'pill--trial', label: 'Oczekujące' },
  confirmed: { bg: 'pill--pending', label: 'Potwierdzone' },
  shipped: { bg: 'text-purple-400 bg-purple-500/15', label: 'Wysłane' },
  delivered: { bg: 'pill--active', label: 'Dostarczone' },
  cancelled: { bg: 'pill--error', label: 'Anulowane' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(amountInCents: number, currency: string) {
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountInCents / 100);
}

function formatOrderCurrency(amount: number, currency: string = 'PLN') {
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount);
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('pl-PL');
}

// ─── Component ───────────────────────────────────────────────────────────────

const MODULE_SUB_STATUS: Record<string, { bg: string; label: string }> = {
  active: { bg: 'pill--active', label: 'Aktywna' },
  trial: { bg: 'pill--trial', label: 'Trial' },
  suspended: { bg: 'pill--suspended', label: 'Zawieszona' },
  cancelled: { bg: 'bg-aurora-bg-subtle text-aurora-muted', label: 'Anulowana' },
};

export function BillingPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'overview' | 'modules' | 'invoices' | 'orders'>(
    'overview'
  );
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [dbPlanPrices, setDbPlanPrices] = useState<DbPlanPrice[]>([]);

  const {
    subscription,
    currentPlan,
    isSubscriptionLoading,
    hasActiveSystemSubscription,
    moduleSubscriptions,
    isModuleSubscriptionsLoading,
    invoices,
    isInvoicesLoading,
    orders,
    isOrdersLoading,
    error,
    maKlientaStripe,
    createCheckoutSession,
    openBillingPortal,
    cancelScheduledChange,
    refresh,
  } = useBilling();
  const { potwierdz: potwierdzZmiany, okno: oknoZmianCennika } = usePotwierdzenieZmianCennika();

  // Przełącznik okresu startuje na okresie OBECNEJ subskrypcji (runda 5):
  // startował zawsze na „Miesięcznie", więc klient roczny klikający wyższy plan
  // dostawał zmianę odłożoną nawet o rok (rok → miesiąc to obniżka od
  // następnego okresu), bez uprzedzenia przed kliknięciem.
  const [okresUstawiony, setOkresUstawiony] = useState(false);
  useEffect(() => {
    if (okresUstawiony || !subscription) return;
    const okres = okresAbonamentu(subscription);
    if (okres) setBillingInterval(okres);
    setOkresUstawiony(true);
  }, [subscription, okresUstawiony]);

  const [odwolywanie, setOdwolywanie] = useState(false);
  const [bladOdwolania, setBladOdwolania] = useState<string | null>(null);
  const handleOdwolajZmiane = async () => {
    try {
      setOdwolywanie(true);
      setBladOdwolania(null);
      await cancelScheduledChange();
      await refresh();
    } catch (err) {
      setBladOdwolania((err as Error).message || 'Nie udało się odwołać zmiany planu');
    } finally {
      setOdwolywanie(false);
    }
  };

  const { currentOrganization } = useAuth();
  const organizationId = currentOrganization?.organization_id || null;

  // Waluta cennika dla tej organizacji: waluta zamrożona na fakturach Stripe
  // (klient już płacący nie może zmienić waluty), a dla nowej organizacji —
  // waluta jej kraju (PL → PLN). Wcześniej strona pokazywała wyłącznie ceny EUR.
  const [krajOrganizacji, setKrajOrganizacji] = useState<string | null>(null);
  useEffect(() => {
    let aktywny = true;
    (async () => {
      if (!organizationId) return;
      const { data } = await supabase
        .from('organizations')
        .select('country')
        .eq('id', organizationId)
        .maybeSingle();
      if (aktywny) setKrajOrganizacji(data?.country ?? null);
    })();
    return () => {
      aktywny = false;
    };
  }, [organizationId]);

  const walutaFaktur = invoices[0]?.currency?.toUpperCase() || null;
  const walutaCennika = walutaFaktur || walutaDlaKraju(krajOrganizacji);

  // Fetch plan prices from DB
  const fetchPlanPrices = useCallback(async () => {
    try {
      const { data, error: err } = await supabase
        .from('plan_prices')
        .select('plan, name_pl, billing_interval, currency, amount, features_pl')
        .eq('is_active', true)
        .gt('amount', 0);

      if (!err && data) {
        setDbPlanPrices(data);
      }
    } catch {
      // Silently fail — fallback to defaults
    }
  }, []);

  useEffect(() => {
    fetchPlanPrices();
  }, [fetchPlanPrices]);

  // Build PLAN_DETAILS dynamically from DB prices in the organization's currency
  const PLAN_DETAILS = { ...DEFAULT_PLAN_DETAILS };
  for (const plan of PLAN_ORDER) {
    if (plan === 'free') continue;
    const monthlyRow = dbPlanPrices.find(
      (p) => p.plan === plan && p.billing_interval === 'monthly' && p.currency === walutaCennika
    );
    const yearlyRow = dbPlanPrices.find(
      (p) => p.plan === plan && p.billing_interval === 'yearly' && p.currency === walutaCennika
    );
    if (monthlyRow || yearlyRow) {
      PLAN_DETAILS[plan] = {
        ...DEFAULT_PLAN_DETAILS[plan],
        name: monthlyRow?.name_pl || yearlyRow?.name_pl || DEFAULT_PLAN_DETAILS[plan].name,
        monthlyPrice: monthlyRow?.amount || DEFAULT_PLAN_DETAILS[plan].monthlyPrice,
        yearlyPrice: yearlyRow?.amount || DEFAULT_PLAN_DETAILS[plan].yearlyPrice,
        features:
          monthlyRow?.features_pl || yearlyRow?.features_pl || DEFAULT_PLAN_DETAILS[plan].features,
        currency: walutaCennika,
      };
    }
  }

  const isInitialLoading = isSubscriptionLoading && isInvoicesLoading && isOrdersLoading;

  const currentPlanIndex = PLAN_ORDER.indexOf(currentPlan);
  const availableUpgrades = PLAN_ORDER.filter((_, i) => i > currentPlanIndex);
  const planDetails = PLAN_DETAILS[currentPlan];
  const planColor = PLAN_COLORS[currentPlan] || PLAN_COLORS.free;

  const pendingInvoices = invoices.filter((i) => i.status === 'open' || i.status === 'draft');
  const pendingInvoicesTotal: KwotyWgWalut = {};
  pendingInvoices.forEach((i) =>
    dodajKwote(pendingInvoicesTotal, i.currency, (i.amount_due || 0) / 100)
  );

  const activeOrders = orders.filter((o) => o.status !== 'delivered' && o.status !== 'cancelled');

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleUpgrade = async (plan: BillingPlan) => {
    setIsRedirecting(true);
    try {
      const url = await createCheckoutSession(plan, billingInterval, potwierdzZmiany);
      if (url) {
        window.location.href = url;
      }
    } finally {
      setIsRedirecting(false);
    }
  };

  const handleManageSubscription = async () => {
    setIsRedirecting(true);
    try {
      const url = await openBillingPortal();
      if (url) {
        window.location.href = url;
      }
    } finally {
      setIsRedirecting(false);
    }
  };

  // ── Loading ─────────────────────────────────────────────────────────────────

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {oknoZmianCennika}
      {/* Header */}
      <div className="pagehead">
        <div>
          <h1 className="pagehead__title flex items-center gap-3">
            <CreditCard className="w-7 h-7 text-green-400" />
            {t('admin.billing.title', 'Rozliczenia')}
          </h1>
          <p className="pagehead__sub">
            {t('admin.billing.subtitle', 'Zarządzaj subskrypcjami, fakturami i zamówieniami')}
          </p>
        </div>
        <div className="pagehead__actions">
          <button onClick={refresh} disabled={isInitialLoading} className="iconbtn">
            <RefreshCw className={`w-5 h-5 ${isInitialLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      {organizationId && (
        <ZmianyCennikaKlienta
          organizationId={organizationId}
          mozeWypowiedziec={['owner', 'admin'].includes(currentOrganization?.role ?? '')}
        />
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-ids-md p-4 flex items-center gap-3 text-red-400">
          <AlertTriangle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="tabs overflow-x-auto" role="tablist">
        {(
          [
            { id: 'overview', label: 'Przegląd', icon: Building2 },
            { id: 'modules', label: 'Moduły', icon: Package },
            { id: 'invoices', label: 'Faktury', icon: Receipt },
            { id: 'orders', label: 'Zamówienia sprzętu', icon: Truck },
          ] as const
        ).map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            role="tab"
            aria-selected={activeTab === id}
            className="flex items-center gap-2 whitespace-nowrap"
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ────────────────── Overview Tab ────────────────── */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Current plan card */}
          <div className={`card border ${planColor.border} p-6`}>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div
                  className={`p-3 rounded-ids-md ${PLAN_COLORS[currentPlan]?.badge || 'bg-gray-500/20'}`}
                >
                  <Crown className={`w-6 h-6 ${planColor.icon}`} />
                </div>
                <div>
                  <p className="overline mb-1">Aktualny plan</p>
                  <h2 className="display text-2xl font-bold text-aurora-text">
                    {planDetails.name}
                  </h2>
                  {subscription ? (
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={`pill ${
                            SUBSCRIPTION_STATUS[subscription.status]?.bg ||
                            'bg-aurora-bg-subtle text-aurora-muted'
                          }`}
                        >
                          <span className="pin" />
                          {SUBSCRIPTION_STATUS[subscription.status]?.label || subscription.status}
                        </span>
                      </div>
                      {subscription.current_period_start && subscription.current_period_end && (
                        <p className="text-sm text-aurora-muted">
                          Okres:{' '}
                          <span className="mono">
                            {formatDate(subscription.current_period_start)} —{' '}
                            {formatDate(subscription.current_period_end)}
                          </span>
                        </p>
                      )}
                      <ZaplanowanaZmianaPlanu
                        wiersz={subscription}
                        nazwa={(plan) => PLAN_DETAILS[plan as BillingPlan]?.name ?? null}
                        onOdwolaj={handleOdwolajZmiane}
                        odwolywanie={odwolywanie}
                        className="mt-2"
                      />
                      {bladOdwolania && <p className="text-sm text-red-400">{bladOdwolania}</p>}
                      {subscription.cancel_at_period_end && (
                        <div className="flex items-center gap-2 mt-2 text-amber-400">
                          <AlertTriangle className="w-4 h-4" />
                          <p className="text-sm">
                            Subskrypcja zostanie anulowana po zakończeniu okresu
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-aurora-muted mt-1">
                      {currentPlan === 'free' ? 'Brak aktywnej subskrypcji' : 'Plan organizacji'}
                    </p>
                  )}
                </div>
              </div>

              {/* Portal płatności — także bez aktywnej subskrypcji, gdy organizacja
                  ma klienta Stripe (runda 5): subskrypcja nieopłacona albo
                  wstrzymana znika z listy aktywnych, a odmowy zakupu 409 kierują
                  właśnie tu. Nazwa ta sama, co w komunikatach serwera. */}
              {(subscription || maKlientaStripe) && (
                <button
                  onClick={handleManageSubscription}
                  disabled={isRedirecting}
                  className="btn btn--secondary"
                >
                  {isRedirecting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ExternalLink className="w-4 h-4" />
                  )}
                  Portal płatności
                </button>
              )}
            </div>
          </div>

          {/* Summary cards */}
          <div className="kpis">
            <div className="statcard">
              <div className="statcard__top">
                <span className="statcard__ico bg-amber-500/20 text-amber-400">
                  <FileText />
                </span>
                <span className="statcard__lbl">Oczekujące faktury</span>
              </div>
              <div className="statcard__num">{pendingInvoices.length}</div>
              <div className="statcard__foot">
                <span className="muted text-xs">
                  {pendingInvoices.length > 0
                    ? `${formatujKwotyWgWalut(pendingInvoicesTotal)} do zapłaty`
                    : 'Brak zaległości'}
                </span>
              </div>
            </div>

            <div className="statcard">
              <div className="statcard__top">
                <span className="statcard__ico">
                  <Package />
                </span>
                <span className="statcard__lbl">Zamówienia sprzętu</span>
              </div>
              <div className="statcard__num">{activeOrders.length}</div>
              <div className="statcard__foot">
                <span className="muted text-xs">
                  {activeOrders.length > 0 ? 'Aktywne zamówienia' : 'Brak aktywnych zamówień'}
                </span>
              </div>
            </div>
          </div>

          {/* Upgrade cards */}
          {availableUpgrades.length > 0 ? (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <h2 className="display text-lg font-semibold text-aurora-text flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-400" />
                  Dostępne plany
                </h2>

                {/* Monthly / Yearly toggle */}
                <div className="segmented" role="group" aria-label="Okres rozliczeniowy">
                  <button
                    onClick={() => setBillingInterval('monthly')}
                    aria-pressed={billingInterval === 'monthly'}
                  >
                    Miesięcznie
                  </button>
                  <button
                    onClick={() => setBillingInterval('yearly')}
                    aria-pressed={billingInterval === 'yearly'}
                    className="relative"
                  >
                    Rocznie
                    <span className="absolute -top-2.5 -right-2 px-1.5 py-0.5 text-[10px] font-bold bg-green-500 text-aurora-on-accent rounded-full leading-none">
                      -17%
                    </span>
                  </button>
                </div>
              </div>

              {billingInterval === 'yearly' && (
                <div className="mb-4 bg-green-500/10 border border-green-500/30 rounded-ids-md px-4 py-3 flex items-center gap-3">
                  <div className="p-1.5 bg-green-500/20 rounded-ids-md">
                    <Zap className="w-4 h-4 text-green-400" />
                  </div>
                  <p className="text-sm text-green-300">
                    <span className="font-semibold">2 miesiące gratis!</span> Płacisz za 10
                    miesięcy, korzystasz przez 12.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {availableUpgrades.map((plan) => {
                  const details = PLAN_DETAILS[plan];
                  const color = PLAN_COLORS[plan] || PLAN_COLORS.free;
                  const displayPrice =
                    billingInterval === 'yearly' ? details.yearlyPrice : details.monthlyPrice;
                  const monthlyEquivalent =
                    billingInterval === 'yearly'
                      ? Math.round(details.yearlyPrice / 12)
                      : details.monthlyPrice;
                  const monthlySavings =
                    billingInterval === 'yearly' ? details.monthlyPrice - monthlyEquivalent : 0;
                  return (
                    <div
                      key={plan}
                      className={`card card--interactive border ${color.border} p-6 flex flex-col`}
                    >
                      <div className="mb-4">
                        <h3 className="display text-lg font-bold text-aurora-text">
                          {details.name}
                        </h3>
                        {/* Cennik jest NETTO, Checkout pobiera brutto (naprawa kasy
                            02.10.2026) — netto zostaje kwotą główną, brutto idzie obok. */}
                        {billingInterval === 'yearly' ? (
                          <div className="mt-2">
                            <p className="mono text-3xl font-bold text-aurora-text">
                              {displayPrice}{' '}
                              <span className="text-sm font-normal text-aurora-muted">
                                {sufiksNetto(details.currency, 'rok')}
                              </span>
                            </p>
                            <p className="mono text-xs text-aurora-muted mt-0.5">
                              {linijkaBrutto(displayPrice, details.currency, 'rok')}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="mono text-sm text-aurora-muted">
                                {monthlyEquivalent} {sufiksNetto(details.currency, 'mies.')}
                              </span>
                              <span className="text-xs font-medium text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded">
                                Oszczędzasz {monthlySavings}{' '}
                                {sufiksNetto(details.currency, 'mies.')}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2">
                            <p className="mono text-3xl font-bold text-aurora-text">
                              {displayPrice}{' '}
                              <span className="text-sm font-normal text-aurora-muted">
                                {sufiksNetto(details.currency, 'mies.')}
                              </span>
                            </p>
                            <p className="mono text-xs text-aurora-muted mt-0.5">
                              {linijkaBrutto(displayPrice, details.currency, 'mies.')}
                            </p>
                          </div>
                        )}
                      </div>
                      <ul className="space-y-2 mb-6 flex-1">
                        {details.features.map((feature) => (
                          <li
                            key={feature}
                            className="flex items-center gap-2 text-sm text-aurora-muted"
                          >
                            <CheckCircle className={`w-4 h-4 ${color.icon} shrink-0`} />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      {zapowiedzZmianyOkresu(subscription, billingInterval) && (
                        <p className="text-xs text-amber-400 mb-3">
                          {zapowiedzZmianyOkresu(subscription, billingInterval)}
                        </p>
                      )}
                      <button
                        onClick={() => handleUpgrade(plan)}
                        disabled={isRedirecting}
                        className="btn btn--primary btn--block"
                      >
                        {isRedirecting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Zap className="w-4 h-4" />
                        )}
                        Wybierz plan
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            currentPlan === 'enterprise' && (
              <div className="card border border-amber-500/30 p-6 text-center">
                <Crown className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                <h3 className="display text-lg font-bold text-aurora-text mb-1">
                  Masz najwyższy plan
                </h3>
                <p className="text-aurora-muted">
                  Korzystasz z planu Enterprise z pełnym dostępem do wszystkich funkcji.
                </p>
              </div>
            )
          )}

          {/* Recent invoices preview */}
          <div className="card overflow-hidden">
            <div className="card__head">
              <h2 className="card__title">Ostatnie faktury</h2>
              <button
                onClick={() => setActiveTab('invoices')}
                className="btn btn--ghost btn--sm ml-auto"
              >
                Zobacz wszystkie
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
            <div className="divide-y divide-aurora-border">
              {isInvoicesLoading ? (
                <div className="p-8 flex justify-center">
                  <Loader2 className="w-6 h-6 text-aurora-muted animate-spin" />
                </div>
              ) : invoices.length === 0 ? (
                <div className="p-8 text-center text-aurora-muted">Brak faktur</div>
              ) : (
                invoices.slice(0, 3).map((invoice) => {
                  const statusConfig = INVOICE_STATUS[invoice.status] || INVOICE_STATUS.draft;
                  const StatusIcon = statusConfig.icon;
                  return (
                    <div
                      key={invoice.id}
                      className="p-4 flex flex-wrap items-center justify-between gap-2"
                    >
                      <div className="flex items-center gap-3">
                        <Receipt className="w-5 h-5 text-aurora-subtle" />
                        <div>
                          <p className="mono text-aurora-text font-medium">
                            {invoice.stripe_invoice_id.length > 20
                              ? `${invoice.stripe_invoice_id.slice(0, 20)}...`
                              : invoice.stripe_invoice_id}
                          </p>
                          <p className="mono text-sm text-aurora-muted">
                            {formatDate(invoice.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="mono text-aurora-text font-medium">
                          {formatCurrency(invoice.amount_due, invoice.currency)}
                        </span>
                        <span className={`pill ${statusConfig.bg}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* ────────────────── Modules Tab ────────────────── */}
      {activeTab === 'modules' && (
        <div className="space-y-4">
          {!hasActiveSystemSubscription && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-ids-md p-4 flex items-center gap-3 text-amber-400">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <div>
                <p className="font-medium">Organizacja na planie Free</p>
                <p className="text-sm text-amber-400/80 mt-1">
                  Część pakietów modułów wymaga planu Universe Start lub wyższego — przy zakupie
                  zobaczysz, które są dostępne w Twoim planie. Plan wybierzesz w zakładce Przegląd.
                </p>
              </div>
            </div>
          )}

          {isModuleSubscriptionsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-aurora-muted animate-spin" />
            </div>
          ) : moduleSubscriptions.length === 0 ? (
            <div className="card p-12 text-center">
              <Package className="w-12 h-12 text-aurora-subtle mx-auto mb-4" />
              <h3 className="display text-lg font-medium text-aurora-text mb-2">
                Brak subskrypcji modułów
              </h3>
              <p className="text-aurora-muted mb-4">
                Przejdź do Marketplace, aby przeglądać i kupować moduły.
              </p>
            </div>
          ) : (
            <div className="card tablecard">
              <table className="table">
                <thead>
                  <tr>
                    <th>Moduł</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>Ważność</th>
                  </tr>
                </thead>
                <tbody>
                  {moduleSubscriptions.map((ms) => {
                    const statusConfig =
                      MODULE_SUB_STATUS[ms.status] || MODULE_SUB_STATUS.cancelled;
                    const isTrialExpired =
                      ms.status === 'trial' &&
                      ms.trial_ends_at &&
                      new Date(ms.trial_ends_at) < new Date();
                    return (
                      <tr key={ms.id}>
                        <td>
                          {/* Nazwa z rejestru, nie identyfikator: `capitalize` robił
                              z `gamification` wycofaną nazwę „Gamification". */}
                          <p className="text-aurora-text font-medium">
                            {getModuleDef(ms.module_id)?.navName ?? ms.module_id}
                          </p>
                        </td>
                        <td>
                          <span className="badge badge--brand capitalize">{ms.plan}</span>
                        </td>
                        <td>
                          <span
                            className={`pill ${isTrialExpired ? 'pill--error' : statusConfig.bg}`}
                          >
                            <span className="pin" />
                            {isTrialExpired ? 'Trial wygasł' : statusConfig.label}
                          </span>
                        </td>
                        <td className="text-sm text-aurora-muted">
                          {ms.status === 'trial' && ms.trial_ends_at
                            ? `Trial do ${formatDate(ms.trial_ends_at)}`
                            : ms.subscription_ends_at
                              ? `Do ${formatDate(ms.subscription_ends_at)}`
                              : ms.plan === 'perpetual'
                                ? 'Licencja wieczysta'
                                : ms.status === 'active'
                                  ? 'Aktywna subskrypcja'
                                  : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ────────────────── Invoices Tab ────────────────── */}
      {activeTab === 'invoices' && (
        <div className="card tablecard">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Numer</th>
                  <th>Data</th>
                  <th>Okres</th>
                  <th>Kwota</th>
                  <th>Status</th>
                  <th className="text-right">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {isInvoicesLoading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <Loader2 className="w-6 h-6 text-aurora-muted animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : invoices.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-aurora-muted">
                      Brak faktur do wyświetlenia
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice) => {
                    const statusConfig = INVOICE_STATUS[invoice.status] || INVOICE_STATUS.draft;
                    const StatusIcon = statusConfig.icon;
                    return (
                      <tr key={invoice.id}>
                        <td>
                          <p className="mono text-aurora-text font-medium text-sm">
                            {invoice.fakturownia_invoice_number ||
                              (invoice.stripe_invoice_id.length > 24
                                ? `${invoice.stripe_invoice_id.slice(0, 24)}...`
                                : invoice.stripe_invoice_id)}
                          </p>
                          {invoice.fakturownia_invoice_id ? (
                            <p className="mono text-xs text-aurora-muted" data-testid="numer-ksef">
                              {invoice.ksef_numer
                                ? `KSeF: ${invoice.ksef_numer}`
                                : 'KSeF: numer w drodze'}
                            </p>
                          ) : null}
                        </td>
                        <td>
                          <p className="mono text-aurora-muted">{formatDate(invoice.created_at)}</p>
                        </td>
                        <td>
                          {invoice.period_start && invoice.period_end ? (
                            <p className="mono text-sm text-aurora-muted">
                              {formatDate(invoice.period_start)} — {formatDate(invoice.period_end)}
                            </p>
                          ) : (
                            <span className="text-aurora-subtle">—</span>
                          )}
                        </td>
                        <td className="num">
                          <p className="text-aurora-text font-medium">
                            {formatCurrency(invoice.amount_due, invoice.currency)}
                          </p>
                          {invoice.amount_paid > 0 &&
                            invoice.amount_paid !== invoice.amount_due && (
                              <p className="text-xs text-aurora-muted">
                                Zapłacono: {formatCurrency(invoice.amount_paid, invoice.currency)}
                              </p>
                            )}
                        </td>
                        <td>
                          <span className={`pill ${statusConfig.bg}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="text-right">
                          <div className="flex items-center justify-end gap-3">
                            {invoice.fakturownia_invoice_url ? (
                              <a
                                href={invoice.fakturownia_invoice_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-aurora-brand hover:text-blue-300 text-sm"
                              >
                                <FileText className="w-4 h-4" />
                                Faktura VAT
                              </a>
                            ) : (
                              <>
                                {invoice.invoice_url && (
                                  <a
                                    href={invoice.invoice_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-aurora-brand hover:text-blue-300 text-sm"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                    Zobacz
                                  </a>
                                )}
                                {invoice.pdf_url && (
                                  <a
                                    href={invoice.pdf_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-aurora-brand hover:text-blue-300 text-sm"
                                  >
                                    <Download className="w-4 h-4" />
                                    PDF
                                  </a>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ────────────────── Hardware Orders Tab ────────────────── */}
      {activeTab === 'orders' && (
        <div className="space-y-4">
          {isOrdersLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-aurora-muted animate-spin" />
            </div>
          ) : orders.length === 0 ? (
            <div className="card p-12 text-center">
              <Package className="w-12 h-12 text-aurora-subtle mx-auto mb-4" />
              <h3 className="display text-lg font-medium text-aurora-text mb-2">
                Brak zamówień sprzętu
              </h3>
              <p className="text-aurora-muted">Zamówienia na urządzenia IoT pojawią się tutaj</p>
            </div>
          ) : (
            orders.map((order) => {
              const statusConfig = ORDER_STATUS[order.status] || ORDER_STATUS.pending;
              return (
                <div key={order.id} className="card p-6">
                  {/* Order header */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-aurora-muted" />
                      <div>
                        <p className="text-aurora-text font-medium">
                          Zamówienie{' '}
                          <span className="mono">
                            {order.shopify_order_number
                              ? `#${order.shopify_order_number}`
                              : `#${order.id.slice(0, 8)}`}
                          </span>
                        </p>
                        <p className="mono text-sm text-aurora-muted">
                          {formatDate(order.created_at)}
                        </p>
                      </div>
                    </div>
                    <span className={`pill ${statusConfig.bg}`}>
                      <span className="pin" />
                      {statusConfig.label}
                    </span>
                  </div>

                  {/* Order items */}
                  {order.items && order.items.length > 0 && (
                    <div className="border-t border-aurora-border pt-4 mb-4">
                      <p className="text-sm text-aurora-muted mb-2">Produkty:</p>
                      <div className="space-y-2">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm">
                            <div className="text-aurora-text">
                              {item.title}
                              {item.variant_title && (
                                <span className="text-aurora-subtle"> ({item.variant_title})</span>
                              )}
                              <span className="text-aurora-subtle"> x{item.quantity}</span>
                            </div>
                            <span className="mono text-aurora-text">
                              {item.price} {order.currency}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Order footer */}
                  <div className="border-t border-aurora-border pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="text-aurora-text font-medium">
                      Łącznie:{' '}
                      <span className="mono">
                        {formatOrderCurrency(order.total_price, order.currency)}
                      </span>
                    </div>

                    {order.tracking_number && (
                      <div className="flex flex-wrap items-center gap-2 text-sm text-aurora-muted">
                        <Truck className="w-4 h-4" />
                        <span>
                          Śledzenie: <span className="mono break-all">{order.tracking_number}</span>
                        </span>
                        {order.tracking_url && (
                          <a
                            href={order.tracking_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-aurora-brand hover:text-blue-300"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
